import { usePersistedState, usePureEffect } from "@buzzeasy/shared-frontend-utilities";
import { Button, Divider, Modal, Typography } from "antd";
import { ReactElement, useCallback, useContext, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { translations } from "../translations";
import { SettingsContext } from "./providers/SettingsContext";

interface FirstLaunchData {
  languageSelected?: boolean;
}

export default function FirstLaunchHandler(): ReactElement {
  const [settings, updateSettings] = useContext(SettingsContext);
  const { t } = useTranslation();

  const [firstLaunchData, setFirstLaunchData] = usePersistedState<FirstLaunchData>({}, "aui-firstLaunchData", localStorage);

  const matchedLanguage = useMemo<string | null>(
    () => {
      const systemLanguage = navigator.language;
      const languageOptions = Object.keys(translations);
      const exactMatch = languageOptions.find(lang => lang === systemLanguage);

      if (exactMatch)
        return exactMatch;

      const languageWithoutRegion = systemLanguage.split("-")[0];
      const languageMatch = languageOptions.find(lang => lang.startsWith(languageWithoutRegion));

      if (languageMatch)
        return languageMatch;

      return null;
    },
    [],
  );

  const isLanguageSupported = !!matchedLanguage;
  const isLanguageSelected = matchedLanguage === settings.language;

  const [showLanguagePrompt, setShowLanguagePrompt] = useState(false);

  const handleLanguageConfirm = useCallback(
    () => {
      if (!matchedLanguage)
        return;

      updateSettings({ language: matchedLanguage });
      setFirstLaunchData(prev => ({ ...prev, languageSelected: true }));
      setShowLanguagePrompt(false);
    },
    [matchedLanguage, setFirstLaunchData, updateSettings],
  );

  const handleLanguageCancel = useCallback(
    () => {
      setFirstLaunchData(prev => ({ ...prev, languageSelected: true }));
      setShowLanguagePrompt(false);
    },
    [setFirstLaunchData],
  );

  usePureEffect(
    (refs) => {
      if (!refs.firstLaunchData.languageSelected && refs.isLanguageSupported && !refs.isLanguageSelected)
        setShowLanguagePrompt(true);
      else if (refs.isLanguageSelected)
        refs.setFirstLaunchData(prev => ({ ...prev, languageSelected: true }));
    },
    [],
    { firstLaunchData, isLanguageSelected, isLanguageSupported, setFirstLaunchData },
  );

  return (
    <>
      {
        (!firstLaunchData.languageSelected && isLanguageSupported && !isLanguageSelected) &&
        <Modal
          open={showLanguagePrompt}
          centered
          onCancel={handleLanguageCancel}
          footer={[
            <Button key="no" onClick={handleLanguageCancel}>
              {t("terms.no", { lng: "en-US" }).capitalize()}
              {" | "}
              {t("terms.no", { lng: matchedLanguage }).capitalize()}
            </Button>,
            <Button key="yes" onClick={handleLanguageConfirm}>
              {t("terms.yes", { lng: "en-US" }).capitalize()}
              {" | "}
              {t("terms.yes", { lng: matchedLanguage }).capitalize()}
            </Button>,
          ]}
        >
          <div lang="en-US">
            <Typography.Title level={4}>
              {t("firstLaunch.languagePrompt.title", { lng: "en-US" })}
            </Typography.Title>
            <Typography.Paragraph>
              <Trans
                i18nKey="firstLaunch.languagePrompt.contentX"
                values={{ value: translations[matchedLanguage].translation.meta.languageInEnglish }}
                tOptions={{ lng: "en-US" }}
              />
            </Typography.Paragraph>
          </div>
          <Divider />
          <div lang={matchedLanguage}>
            <Typography.Title level={4}>
              {t("firstLaunch.languagePrompt.title", { lng: matchedLanguage })}
            </Typography.Title>
            <Typography.Paragraph>
              <Trans
                i18nKey="firstLaunch.languagePrompt.contentX"
                values={{ value: translations[matchedLanguage].translation.meta.languageInNative }}
                tOptions={{ lng: matchedLanguage }}
              />
            </Typography.Paragraph>
          </div>
        </Modal>
      }
    </>
  );
}
